import { camelCase, set } from 'lodash';

import AtsValidationError, {
  AtsValidationErrorMessage,
  AtsValidationErrors,
  IetfError,
  ValidationError,
  ValidationErrorMap,
} from '@models/ValidationError';

const getEmptyValidationErrorMap = (): ValidationErrorMap => ({
  fieldErrors: {},
  generalErrors: [],
});

export const isAtsValidationError = (data?: unknown): data is AtsValidationErrors => {
  if (!data || typeof data !== 'object') return false;
  return 'Code' in data && 'Message' in data && 'Description' in data;
};

export const isIetfValidationError = (data?: unknown): data is IetfError => {
  if (!data || typeof data !== 'object') return false;
  return (
    'type' in data && 'title' in data && 'status' in data && 'errors' in data && 'traceId' in data
  );
};

export const isValidationError = (data?: unknown): data is ValidationError =>
  isAtsValidationError(data) || isIetfValidationError(data);

export const isValidationErrorList = (
  message?: AtsValidationErrorMessage
): message is AtsValidationError[] => Boolean(message && Array.isArray(message));

export const isValidationErrorStr = (message?: AtsValidationErrorMessage): message is string =>
  Boolean(message && typeof message === 'string');

/**
 * Separates validation errors into field specific and general errors in order to display field errors in relation to their respective fields
 * @param fieldNames Names of the fields that potentially has validation errors
 * @param atsErrorMessage Validation error array
 * @param ietfError IETF style validation error object
 * @returns Validation errors mapped into `fieldErrors` and `generalErrors`
 */
export const getValidationErrorMap = (
  fieldNames: string[],
  atsErrorMessage?: AtsValidationErrorMessage,
  ietfError?: IetfError
): ValidationErrorMap => {
  if (atsErrorMessage) {
    if (isValidationErrorStr(atsErrorMessage))
      return { fieldErrors: {}, generalErrors: atsErrorMessage };

    return atsErrorMessage.reduce(
      (acc: ValidationErrorMap, cur) =>
        fieldNames.includes(cur.Field)
          ? set(acc, `fieldErrors.${cur.Field}`, cur.Message)
          : { ...acc, generalErrors: [...(acc.generalErrors as AtsValidationError[]), cur] },
      getEmptyValidationErrorMap()
    );
  }

  if (ietfError)
    return Object.entries(ietfError.errors).reduce((acc: ValidationErrorMap, [key, value]) => {
      const fieldName = camelCase(key);

      if (fieldNames.includes(fieldName)) return set(acc, `fieldErrors.${fieldName}`, value[0]);

      return {
        ...acc,
        generalErrors: [
          ...(acc.generalErrors as AtsValidationError[]),
          {
            Children: null,
            Field: fieldName,
            Index: 0,
            Message: value[0],
          },
        ],
      };
    }, getEmptyValidationErrorMap());

  return getEmptyValidationErrorMap();
};

export const menuFieldNames = ['name', 'slug', 'deviceTypeId'];
export const pageFieldNames = ['name.no', 'name.en', 'name.da', 'slug'];
export const collectionFieldNames = ['name.no', 'name.en', 'name.da', 'slug', 'description'];
